import { parse } from 'graphql'
import { useQuery } from 'glimmer-apollo'

export type FlowConfigResponse = {
  flowConfig: FlowConfigSuccess | NotFoundError
}
export type FlowConfigSuccess = {
  __typename: 'FlowConfigSuccess'
  flowConfig: FlowConfig
}
export type FlowConfigQueryVariables = {
  id: string
}

export async function fetchFlowConfig(
  context: Object,
  variables: FlowConfigQueryVariables
): Promise<FlowConfigResponse | undefined | null> {
  return new Promise((resolve, reject) => {
    const programQuery = useQuery<FlowConfigResponse, FlowConfigQueryVariables>(context, () => [
      query,
      {
        variables,
        fetchPolicy: 'no-cache',
        onComplete: (data: FlowConfigResponse | undefined | null) => {
          resolve(data)
        },
        onError: (error) => {
          reject(new Error(`Failed to fetch flow configuration. ${error.message}`, { cause: error }))
        }
      }
    ])

    programQuery.refetch()
  })
}

const query = parse(`
  query FlowConfig($id: ID!) {
    flowConfig(id: $id) {
      ... on FlowConfigSuccess {
        __typename
        flowConfig {
          program {
            ...LegacyProgramFragment
          }
          pages {
            __typename
            slots
            visible
            ... on FlowConfigBookingStartPage {
              title
              description
              images
              overrideCallToBookText
              minShowLeftCutoff
            }
            ... on FlowConfigBookingPaymentPage {
              countdownEnabled
              externalTocLink
              externalTocName
              promocodeLinkText
            }

            steps {
              __typename
              renderGroup
              visible
              ... on FlowConfigDateStep {
                calendarStartDate
                numDaysToShow
              }
              ... on FlowConfigTicketStep {
                title
                initialSelectionDescription
              }
              ... on FlowConfigTimeStep {
                title
                showBookedOut
              }
              ... on FlowConfigDurationStep {
                title
              }
              ... on FlowConfigConfirmationStep {
                title
                description
              }
              ... on FlowConfigBookingPortalStep {
                description
                linkLabel
              }
              ... on FlowConfigCustomQuestionsStep {
                questionActivityConfigurations {
                  id
                  isRequired
                  question {
                    ...QuestionFragment
                  }
                }
              }
              ... on FlowConfigRelatedBookingFlowsStep {
                alwaysVisible              
              }
            }
          }
        }
      }

      ... on Error {
        __typename
        message
      }
    }
  }
  fragment QuestionFragment on Question {
    id
    text
    type
    perGuest
    hint
    answerDefaultValue
    whitelistedCategories
    ... on ChoiceQuestion {
      options {
        id
        text
        hint
        chargePerGuest
        itemOption {
          id
          name
          price {
            amount
            currency
            formatted
          }
          item {
            id
          }
        }
        followupQuestions {
          id
          text
          type
          perGuest
          hint
          answerDefaultValue
          ... on ChoiceQuestion {
            options {
              id
              text
              hint
              chargePerGuest
              itemOption {
                id
                name
                price {
                  amount
                  currency
                  formatted
                }
                item {
                  id
                }
              }
              # GQL has no recursion by default, so we need to manually query 5 times for follow-up questions
              # if a partner has MORE than 5 levels of depth for their follow-up questions, add more nesting here
              followupQuestions {
                id
                text
                type
                perGuest
                hint
                answerDefaultValue
                ... on ChoiceQuestion {
                  options {
                    id
                    text
                    hint
                    chargePerGuest
                    itemOption {
                      id
                      name
                      price {
                        amount
                        currency
                        formatted
                      }
                      item {
                        id
                      }
                    }
                    followupQuestions {
                      id
                      text
                      type
                      perGuest
                      hint
                      answerDefaultValue
                      ... on ChoiceQuestion {
                        options {
                          id
                          text
                          hint
                          chargePerGuest
                          itemOption {
                            id
                            name
                            price {
                              amount
                              currency
                              formatted
                            }
                            item {
                              id
                            }
                          }
                          followupQuestions {
                            id
                            text
                            type
                            perGuest
                            hint
                            answerDefaultValue
                            ... on ChoiceQuestion {
                              options {
                                id
                                text
                                hint
                                chargePerGuest
                                itemOption {
                                  id
                                  name
                                  price {
                                    amount
                                    currency
                                    formatted
                                  }
                                }
                                followupQuestions {
                                  id
                                  text
                                  type
                                  perGuest
                                  hint
                                  answerDefaultValue
                                  ... on ChoiceQuestion {
                                    options {
                                      id
                                      text
                                      hint
                                      chargePerGuest
                                      itemOption {
                                        id
                                        name
                                        price {
                                          amount
                                          currency
                                          formatted
                                        }
                                        item {
                                          id
                                        }
                                      }
                                      followupQuestions {
                                        id
                                        text
                                        type
                                        perGuest
                                        hint
                                        answerDefaultValue
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment LegacyProgramFragment on Program {
    id
    layoutName
    locale
    pricingTransparency
    provider {
      description
      name
      id
    }
    tracking {
      ga
      fb
    }
    theme
    ... on ProgramTiles {
      title
      pricingTransparency
      tiles {
        image
        subtitle
        title
        program {
          layoutName
          id
          ... on BookingFlow {
            activity {
              id
              name
              description
              imageUrls
              type
            }
          }
        }
      }
    }
    ... on BookingFlow {
      orderTaxesFeesBreakdownMode
      relatedPrograms {
        id
        cta
        title
        subtitle
        program {
          id
          layoutName
        }
      }
      ignoreBookingCutoff
      disableAutoAddTicket
      disableCountdownClock
      enableMembershipRedemption
      hideDateTime
      overrideFreesaleText
      overrideTextTicketEach
      overrideSoldOutText
      overrideCallToBookText
      optinMarketingEnabled
      optinMarketingDescription
      optinMarketingTitle
      minShowLeftCutoff
      ticketSelection
      showTicketSection
      calendarStartDate
      timeSelectionMode
      smsConfig {
        enabled
        optinText
        privacyPolicyUrl
        termsUrl
      }
      activity {
        id
        name
        description
        imageUrls
        type
        minimumBookingBannerText
        tickets {
          id
          name
          description
          imageUrl
          category
          minQuantity
          maxQuantity
          isMinRequired
        }
        reviewMeta {
          count
          avgRating
          fiveStar
        }
        reviews(pageSize: 3, onlyReviewsWithComments: true, ratings: [5]) {
          entries {
            ... on Review {
              id
              name
              comment
              reviewedAt
            }
          }
        }
      }
      promoCodeDisplay
      promoCodeLocation
      bundleItems {
        id
        name
        description
        image
        isRequired
        bundle {
          id
        }
        product {
          __typename
          id
          name
          description
          imageUrls
          ... on Activity {
            type
          }
        }
        tickets {
          id
          name
          description
          imageUrl
        }
        ticketQuantityMatchMode
        canChangeTime
        allowOverlap
        mustMatchParentDuration
        mustMatchParentStartTime
        availabilityDefaultSelection
        availabilitySelectPrompt
        availabilityTimeVisibilityMode
        availabilityStartOffsetMode
        availabilityStartOffsetDays
        availabilityStartOffsetMinutes
        availabilityEndOffsetMode
        availabilityEndOffsetDays
        availabilityEndOffsetMinutes
        availabilityEndOffsetRelativeTo
      }
      languageOverrides {
        key
        value
      }
    }
  }
`)
